/**
 * Control Tower Domain Seed Data
 *
 * Contains all seed data for Control Tower Service (19):
 * - Test Scenarios (E2E test definitions)
 * - Incidents (failure tracking)
 * - Alert Configurations
 * - Service Health Snapshots
 * - Signals and Baselines
 * - E2E Scenarios and Executions
 *
 * @see Issue #108 - Control Tower Integration
 * @see 19-control-tower-service/prisma/schema.prisma
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// TEST SCENARIO IDs
// =============================================================================

export const TEST_SCENARIO_IDS = {
  // Critical path scenarios
  RAG_PIPELINE_CRITICAL: seedId('test-scenario-rag-pipeline', 1),
  AUTH_FLOW_CRITICAL: seedId('test-scenario-auth-flow', 2),
  LOAN_SUBMISSION_E2E: seedId('test-scenario-loan-submit', 3),

  // Health check scenarios
  SERVICE_HEALTH_CHECK: seedId('test-scenario-health-check', 4),
  DATABASE_CONNECTIVITY: seedId('test-scenario-db-connect', 5),

  // Workflow scenarios
  DOCUMENT_INGESTION: seedId('test-scenario-doc-ingest', 6),
  NOTIFICATION_DELIVERY: seedId('test-scenario-notification', 7),
  VOICE_CALL_FLOW: seedId('test-scenario-voice-call', 8),
} as const;

export type TestScenarioId = (typeof TEST_SCENARIO_IDS)[keyof typeof TEST_SCENARIO_IDS];

// =============================================================================
// INCIDENT IDs
// =============================================================================

export const INCIDENT_IDS = {
  // OpsMatrix Capital incidents
  RAG_TIMEOUT_INCIDENT: seedId('incident-rag-timeout', 1),
  DB_CONNECTION_INCIDENT: seedId('incident-db-conn', 2),
  API_RATE_LIMIT_INCIDENT: seedId('incident-rate-limit', 3),
  MODEL_ERROR_INCIDENT: seedId('incident-model-error', 4),

  // 1Sharpe Capital incidents
  SHARPE_DOC_UPLOAD_FAILURE: seedId('incident-sharpe-doc', 1),
  SHARPE_DRAW_CALC_ERROR: seedId('incident-sharpe-draw', 2),

  // Pacific Bridge Fund incidents
  PACIFIC_REPORT_GENERATION: seedId('incident-pacific-report', 1),
} as const;

export type IncidentId = (typeof INCIDENT_IDS)[keyof typeof INCIDENT_IDS];

// =============================================================================
// ALERT CONFIGURATION IDs
// =============================================================================

export const ALERT_CONFIG_IDS = {
  OPSMATRIX_ALERTS: seedId('alert-config-opsmatrix', 1),
  SHARPE_ALERTS: seedId('alert-config-sharpe', 2),
  PACIFIC_ALERTS: seedId('alert-config-pacific', 3),
} as const;

export type AlertConfigId = (typeof ALERT_CONFIG_IDS)[keyof typeof ALERT_CONFIG_IDS];

// =============================================================================
// SIGNAL BASELINE IDs
// =============================================================================

export const SIGNAL_BASELINE_IDS = {
  AGENT_LATENCY: seedId('baseline-agent-latency', 1),
  RETRIEVAL_LATENCY: seedId('baseline-retrieval-latency', 2),
  MODEL_GATEWAY_LATENCY: seedId('baseline-model-latency', 3),
  DATABASE_QUERY_LATENCY: seedId('baseline-db-latency', 4),
} as const;

export type SignalBaselineId = (typeof SIGNAL_BASELINE_IDS)[keyof typeof SIGNAL_BASELINE_IDS];

// =============================================================================
// E2E SCENARIO IDs
// =============================================================================

export const E2E_SCENARIO_IDS = {
  FULL_LOAN_FLOW: seedId('e2e-full-loan-flow', 1),
  DOCUMENT_PROCESSING: seedId('e2e-doc-processing', 2),
  CONSUMER_ONBOARDING: seedId('e2e-consumer-onboard', 3),
  AI_CHAT_SESSION: seedId('e2e-ai-chat', 4),
  VOICE_CAMPAIGN_FLOW: seedId('e2e-voice-campaign', 5),
} as const;

export type E2EScenarioId = (typeof E2E_SCENARIO_IDS)[keyof typeof E2E_SCENARIO_IDS];

// =============================================================================
// TEST RUN IDs (Sample executions)
// =============================================================================

export const TEST_RUN_IDS = {
  // Recent RAG pipeline runs
  RAG_RUN_SUCCESS_1: seedId('testrun-rag-success', 1),
  RAG_RUN_SUCCESS_2: seedId('testrun-rag-success', 2),
  RAG_RUN_FAILED: seedId('testrun-rag-failed', 1),
  RAG_RUN_TIMEOUT: seedId('testrun-rag-timeout', 1),

  // Auth flow runs
  AUTH_RUN_SUCCESS_1: seedId('testrun-auth-success', 1),
  AUTH_RUN_SUCCESS_2: seedId('testrun-auth-success', 2),

  // Health check runs
  HEALTH_RUN_SUCCESS: seedId('testrun-health-success', 1),
  HEALTH_RUN_FAILED: seedId('testrun-health-failed', 1),

  // E2E scenario execution runs
  E2E_LOAN_FLOW_RUN: seedId('testrun-e2e-loan', 1),
  E2E_DOC_PROCESSING_RUN: seedId('testrun-e2e-doc', 1),
} as const;

export type TestRunId = (typeof TEST_RUN_IDS)[keyof typeof TEST_RUN_IDS];

// =============================================================================
// TEST RUN STATUS (for seed data - using same values as TestRunStatus enum)
// =============================================================================
// Note: Use TestRunStatus enum from enums/sla.enums.ts for type-safe validation.
// This const is kept for backwards compatibility with existing seed scripts.

export const SEED_TEST_RUN_STATUSES = {
  RUNNING: 'RUNNING',
  PASSED: 'PASSED',
  FAILED: 'FAILED',
  TIMEOUT: 'TIMEOUT',
  CANCELLED: 'CANCELLED',
} as const;

export type SeedTestRunStatus = (typeof SEED_TEST_RUN_STATUSES)[keyof typeof SEED_TEST_RUN_STATUSES];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface TestScenarioSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  definition: {
    nodes: Array<{
      id: string;
      type: string;
      config: Record<string, unknown>;
      next?: string[];
    }>;
    startNode: string;
  };
  tags: string[];
  schedule?: string;
  alertOnFailure: boolean;
  enabled: boolean;
  timeoutMs: number;
  createdBy: string;
  updatedBy: string;
}

export interface IncidentSeedData {
  id: string;
  testRunId?: string;
  businessId: string;
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  service?: string;
  message: string;
  details: Record<string, unknown>;
  alertsSent?: Record<string, boolean>;
  acknowledgedAt?: string;
  resolvedAt?: string;
}

export interface AlertConfigSeedData {
  id: string;
  businessId: string;
  slackWebhookSecretRef?: string;
  emailAddresses?: string;
  pagerDutySecretRef?: string;
  webhookSecretRef?: string;
  criticalLevel: number;
  warningLevel: number;
  enableSlack: boolean;
  enableEmail: boolean;
  enablePagerDuty: boolean;
  enableWebhook: boolean;
}

export interface SignalBaselineSeedData {
  id: string;
  businessId: string;
  serviceName: string;
  signalType: string;
  baselineValue: number;
  stdDeviation: number;
  percentile95: number;
  percentile99: number;
  minValue: number;
  maxValue: number;
  sampleCount: number;
}

export interface E2EScenarioSeedData {
  id: string;
  businessId: string;
  scenarioCode: string;
  name: string;
  description?: string;
  serviceFlow: string[];
  signalThresholds: Record<string, { max?: number; min?: number; p99?: number }>;
  maxDurationMs: number;
  assertions: Array<{
    type: string;
    path: string;
    operator: 'eq' | 'gt' | 'lt' | 'contains';
    value: unknown;
  }>;
  enabled: boolean;
  priority: number;
  tags: string[];
}

// =============================================================================
// TEST SCENARIO DATA
// =============================================================================

export const TEST_SCENARIOS: Record<keyof typeof TEST_SCENARIO_IDS, TestScenarioSeedData> = {
  RAG_PIPELINE_CRITICAL: {
    id: TEST_SCENARIO_IDS.RAG_PIPELINE_CRITICAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'RAG Pipeline Critical Path',
    description: 'Tests the critical RAG pipeline path from query to response',
    definition: {
      startNode: 'query',
      nodes: [
        { id: 'query', type: 'input', config: { prompt: 'What are the loan requirements?' }, next: ['retrieve'] },
        { id: 'retrieve', type: 'retrieval', config: { topK: 5, threshold: 0.7 }, next: ['generate'] },
        { id: 'generate', type: 'llm', config: { model: 'gpt-4', temperature: 0.7 }, next: ['validate'] },
        { id: 'validate', type: 'assertion', config: { minLength: 100, maxLatencyMs: 5000 } },
      ],
    },
    tags: ['critical', 'rag', 'p0'],
    schedule: '0 * * * *', // hourly
    alertOnFailure: true,
    enabled: true,
    timeoutMs: 60000,
    createdBy: USER_IDS.SUPER_ADMIN,
    updatedBy: USER_IDS.SUPER_ADMIN,
  },

  AUTH_FLOW_CRITICAL: {
    id: TEST_SCENARIO_IDS.AUTH_FLOW_CRITICAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Authentication Flow Critical',
    description: 'Tests the full authentication flow including token exchange',
    definition: {
      startNode: 'login',
      nodes: [
        { id: 'login', type: 'http', config: { endpoint: '/auth/login', method: 'POST' }, next: ['exchange'] },
        { id: 'exchange', type: 'http', config: { endpoint: '/auth/exchange', method: 'POST' }, next: ['validate'] },
        { id: 'validate', type: 'assertion', config: { hasAccessToken: true, hasRefreshToken: true } },
      ],
    },
    tags: ['critical', 'auth', 'security', 'p0'],
    schedule: '*/15 * * * *', // every 15 min
    alertOnFailure: true,
    enabled: true,
    timeoutMs: 30000,
    createdBy: USER_IDS.SUPER_ADMIN,
    updatedBy: USER_IDS.SUPER_ADMIN,
  },

  LOAN_SUBMISSION_E2E: {
    id: TEST_SCENARIO_IDS.LOAN_SUBMISSION_E2E,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Submission E2E',
    description: 'End-to-end test for loan submission workflow',
    definition: {
      startNode: 'create-consumer',
      nodes: [
        {
          id: 'create-consumer',
          type: 'api',
          config: { resource: 'consumers', action: 'create' },
          next: ['create-loan'],
        },
        { id: 'create-loan', type: 'api', config: { resource: 'loans', action: 'create' }, next: ['upload-docs'] },
        { id: 'upload-docs', type: 'api', config: { resource: 'documents', action: 'upload' }, next: ['submit'] },
        { id: 'submit', type: 'api', config: { resource: 'loans', action: 'submit' }, next: ['verify'] },
        { id: 'verify', type: 'assertion', config: { status: 'SUBMITTED' } },
      ],
    },
    tags: ['e2e', 'loan', 'workflow'],
    schedule: '0 */4 * * *', // every 4 hours
    alertOnFailure: true,
    enabled: true,
    timeoutMs: 120000,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    updatedBy: USER_IDS.RACHEL_MARTINEZ,
  },

  SERVICE_HEALTH_CHECK: {
    id: TEST_SCENARIO_IDS.SERVICE_HEALTH_CHECK,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Service Health Check',
    description: 'Checks health endpoints of all critical services',
    definition: {
      startNode: 'gateway',
      nodes: [
        { id: 'gateway', type: 'health', config: { service: 'api-gateway', port: 3000 }, next: ['account'] },
        { id: 'account', type: 'health', config: { service: 'account-service', port: 50065 }, next: ['agent'] },
        { id: 'agent', type: 'health', config: { service: 'agent-service', port: 50070 }, next: ['retrieval'] },
        { id: 'retrieval', type: 'health', config: { service: 'retrieval-service', port: 50071 }, next: ['validate'] },
        { id: 'validate', type: 'assertion', config: { allHealthy: true } },
      ],
    },
    tags: ['health', 'infra', 'p0'],
    schedule: '*/5 * * * *', // every 5 min
    alertOnFailure: true,
    enabled: true,
    timeoutMs: 30000,
    createdBy: USER_IDS.SUPER_ADMIN,
    updatedBy: USER_IDS.SUPER_ADMIN,
  },

  DATABASE_CONNECTIVITY: {
    id: TEST_SCENARIO_IDS.DATABASE_CONNECTIVITY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Database Connectivity Check',
    description: 'Verifies database connectivity across all services',
    definition: {
      startNode: 'gateway-db',
      nodes: [
        { id: 'gateway-db', type: 'db-ping', config: { database: 'ops_matrix_gateway' }, next: ['account-db'] },
        { id: 'account-db', type: 'db-ping', config: { database: 'ops_matrix_account' }, next: ['case-db'] },
        { id: 'case-db', type: 'db-ping', config: { database: 'ops_matrix_case_mgmt' }, next: ['validate'] },
        { id: 'validate', type: 'assertion', config: { allConnected: true, maxLatencyMs: 100 } },
      ],
    },
    tags: ['database', 'infra', 'connectivity'],
    schedule: '*/10 * * * *', // every 10 min
    alertOnFailure: true,
    enabled: true,
    timeoutMs: 30000,
    createdBy: USER_IDS.SUPER_ADMIN,
    updatedBy: USER_IDS.SUPER_ADMIN,
  },

  DOCUMENT_INGESTION: {
    id: TEST_SCENARIO_IDS.DOCUMENT_INGESTION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Document Ingestion Pipeline',
    description: 'Tests document upload, processing, and vector embedding',
    definition: {
      startNode: 'upload',
      nodes: [
        { id: 'upload', type: 'api', config: { resource: 'documents', action: 'upload' }, next: ['process'] },
        { id: 'process', type: 'wait', config: { event: 'document.processed', timeoutMs: 30000 }, next: ['embed'] },
        { id: 'embed', type: 'api', config: { resource: 'embeddings', action: 'create' }, next: ['search'] },
        { id: 'search', type: 'api', config: { resource: 'search', action: 'query' }, next: ['validate'] },
        { id: 'validate', type: 'assertion', config: { hasResults: true, relevanceScore: 0.8 } },
      ],
    },
    tags: ['document', 'rag', 'embedding'],
    schedule: '0 */2 * * *', // every 2 hours
    alertOnFailure: true,
    enabled: true,
    timeoutMs: 120000,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    updatedBy: USER_IDS.RACHEL_MARTINEZ,
  },

  NOTIFICATION_DELIVERY: {
    id: TEST_SCENARIO_IDS.NOTIFICATION_DELIVERY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Notification Delivery Test',
    description: 'Tests notification dispatch across all channels',
    definition: {
      startNode: 'send',
      nodes: [
        {
          id: 'send',
          type: 'api',
          config: { resource: 'notifications', action: 'send', channels: ['email', 'in_app'] },
          next: ['verify-email'],
        },
        { id: 'verify-email', type: 'wait', config: { event: 'email.sent', timeoutMs: 10000 }, next: ['verify-inapp'] },
        {
          id: 'verify-inapp',
          type: 'wait',
          config: { event: 'notification.delivered', timeoutMs: 5000 },
          next: ['validate'],
        },
        { id: 'validate', type: 'assertion', config: { allDelivered: true } },
      ],
    },
    tags: ['notification', 'email', 'delivery'],
    schedule: '0 */6 * * *', // every 6 hours
    alertOnFailure: true,
    enabled: true,
    timeoutMs: 60000,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    updatedBy: USER_IDS.RACHEL_MARTINEZ,
  },

  VOICE_CALL_FLOW: {
    id: TEST_SCENARIO_IDS.VOICE_CALL_FLOW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Voice Call Flow Test',
    description: 'Tests voice call initiation and AI agent response',
    definition: {
      startNode: 'initiate',
      nodes: [
        { id: 'initiate', type: 'api', config: { resource: 'voice-calls', action: 'initiate' }, next: ['connect'] },
        { id: 'connect', type: 'wait', config: { event: 'call.connected', timeoutMs: 30000 }, next: ['interact'] },
        {
          id: 'interact',
          type: 'voice',
          config: { transcript: 'Hello, I need help with my loan', expectResponse: true },
          next: ['validate'],
        },
        { id: 'validate', type: 'assertion', config: { callDuration: { min: 5000 }, aiResponseReceived: true } },
      ],
    },
    tags: ['voice', 'ai', 'retell'],
    schedule: '0 8 * * *', // daily at 8 AM
    alertOnFailure: true,
    enabled: true,
    timeoutMs: 120000,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    updatedBy: USER_IDS.RACHEL_MARTINEZ,
  },
};

// =============================================================================
// INCIDENT DATA (Sample historical incidents)
// =============================================================================

export const INCIDENTS: Record<keyof typeof INCIDENT_IDS, IncidentSeedData> = {
  RAG_TIMEOUT_INCIDENT: {
    id: INCIDENT_IDS.RAG_TIMEOUT_INCIDENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    severity: 'CRITICAL',
    service: 'retrieval-service',
    message: 'RAG pipeline timeout exceeded - retrieval service not responding',
    details: {
      nodeId: 'retrieve',
      nodeType: 'retrieval',
      errorMessage: 'Timeout after 30000ms',
      expectedLatencyMs: 5000,
      actualLatencyMs: 30000,
    },
    alertsSent: { slack: true, email: true, pagerDuty: false },
    resolvedAt: '2026-01-15T10:30:00Z',
  },

  DB_CONNECTION_INCIDENT: {
    id: INCIDENT_IDS.DB_CONNECTION_INCIDENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    severity: 'WARNING',
    service: 'case-management-service',
    message: 'Database connection pool exhausted',
    details: {
      nodeId: 'case-db',
      errorMessage: 'Connection pool limit reached (max: 10, waiting: 15)',
      poolSize: 10,
      waitingConnections: 15,
    },
    alertsSent: { slack: true, email: false },
    acknowledgedAt: '2026-01-18T14:00:00Z',
  },

  API_RATE_LIMIT_INCIDENT: {
    id: INCIDENT_IDS.API_RATE_LIMIT_INCIDENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    severity: 'INFO',
    service: 'api-gateway',
    message: 'API rate limit threshold approaching (80% utilized)',
    details: {
      endpoint: '/api/v1/loans',
      currentRate: 800,
      maxRate: 1000,
      window: '1m',
    },
    alertsSent: { slack: true },
  },

  MODEL_ERROR_INCIDENT: {
    id: INCIDENT_IDS.MODEL_ERROR_INCIDENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    severity: 'CRITICAL',
    service: 'model-gateway-service',
    message: 'OpenAI API returned 500 error - model unavailable',
    details: {
      nodeId: 'generate',
      nodeType: 'llm',
      model: 'gpt-4',
      errorCode: 500,
      errorMessage: 'Internal server error from OpenAI',
      retryCount: 3,
    },
    alertsSent: { slack: true, email: true, pagerDuty: true },
    acknowledgedAt: '2026-01-20T09:15:00Z',
    resolvedAt: '2026-01-20T09:45:00Z',
  },

  // ==========================================================================
  // 1SHARPE CAPITAL INCIDENTS
  // ==========================================================================
  SHARPE_DOC_UPLOAD_FAILURE: {
    id: INCIDENT_IDS.SHARPE_DOC_UPLOAD_FAILURE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    severity: 'WARNING',
    service: 'case-management-service',
    message: 'Document upload failed - S3 bucket permission denied',
    details: {
      bucket: 'sharpe-construction-docs',
      operation: 'PutObject',
      errorCode: 'AccessDenied',
      documentType: 'inspection_photo',
      fileSize: 15728640, // 15MB
    },
    alertsSent: { slack: true },
    acknowledgedAt: '2026-01-21T11:30:00Z',
  },
  SHARPE_DRAW_CALC_ERROR: {
    id: INCIDENT_IDS.SHARPE_DRAW_CALC_ERROR,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    severity: 'WARNING',
    service: 'billing-service',
    message: 'Construction draw calculation mismatch detected',
    details: {
      drawNumber: 3,
      expectedAmount: 125000,
      calculatedAmount: 118750,
      variance: 6250,
      loanId: 'loan-sharpe-const-001',
      reason: 'Retainage calculation discrepancy',
    },
    alertsSent: { slack: true, email: true },
    acknowledgedAt: '2026-01-20T14:00:00Z',
    resolvedAt: '2026-01-20T16:30:00Z',
  },

  // ==========================================================================
  // PACIFIC BRIDGE FUND INCIDENTS
  // ==========================================================================
  PACIFIC_REPORT_GENERATION: {
    id: INCIDENT_IDS.PACIFIC_REPORT_GENERATION,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    severity: 'INFO',
    service: 'reporting-service',
    message: 'Monthly investor report generation delayed',
    details: {
      reportType: 'monthly_investor_summary',
      reportPeriod: '2026-01',
      expectedDelivery: '2026-01-20T09:00:00Z',
      actualDelivery: '2026-01-20T14:00:00Z',
      reason: 'Extended data reconciliation required',
      affectedInvestors: 12,
    },
    alertsSent: { email: true },
    resolvedAt: '2026-01-20T14:00:00Z',
  },
};

// =============================================================================
// ALERT CONFIGURATION DATA
// =============================================================================

export const ALERT_CONFIGS: Record<keyof typeof ALERT_CONFIG_IDS, AlertConfigSeedData> = {
  OPSMATRIX_ALERTS: {
    id: ALERT_CONFIG_IDS.OPSMATRIX_ALERTS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    slackWebhookSecretRef: 'secrets/slack/webhook-opsmatrix',
    emailAddresses: 'ops-alerts@opsmatrix.local,engineering@opsmatrix.local',
    pagerDutySecretRef: 'secrets/pagerduty/opsmatrix',
    criticalLevel: 1,
    warningLevel: 5,
    enableSlack: true,
    enableEmail: true,
    enablePagerDuty: true,
    enableWebhook: false,
  },

  SHARPE_ALERTS: {
    id: ALERT_CONFIG_IDS.SHARPE_ALERTS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    emailAddresses: 'alerts@1sharpe.local',
    criticalLevel: 1,
    warningLevel: 10,
    enableSlack: false,
    enableEmail: true,
    enablePagerDuty: false,
    enableWebhook: false,
  },

  PACIFIC_ALERTS: {
    id: ALERT_CONFIG_IDS.PACIFIC_ALERTS,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    slackWebhookSecretRef: 'secrets/slack/webhook-pacific',
    criticalLevel: 1,
    warningLevel: 5,
    enableSlack: true,
    enableEmail: false,
    enablePagerDuty: false,
    enableWebhook: false,
  },
};

// =============================================================================
// SIGNAL BASELINE DATA
// =============================================================================

export const SIGNAL_BASELINES: Record<keyof typeof SIGNAL_BASELINE_IDS, SignalBaselineSeedData> = {
  AGENT_LATENCY: {
    id: SIGNAL_BASELINE_IDS.AGENT_LATENCY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    serviceName: 'agent-service',
    signalType: 'execution_latency_ms',
    baselineValue: 1500,
    stdDeviation: 300,
    percentile95: 2200,
    percentile99: 3500,
    minValue: 500,
    maxValue: 5000,
    sampleCount: 10000,
  },

  RETRIEVAL_LATENCY: {
    id: SIGNAL_BASELINE_IDS.RETRIEVAL_LATENCY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    serviceName: 'retrieval-service',
    signalType: 'query_latency_ms',
    baselineValue: 250,
    stdDeviation: 75,
    percentile95: 400,
    percentile99: 600,
    minValue: 50,
    maxValue: 1000,
    sampleCount: 50000,
  },

  MODEL_GATEWAY_LATENCY: {
    id: SIGNAL_BASELINE_IDS.MODEL_GATEWAY_LATENCY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    serviceName: 'model-gateway-service',
    signalType: 'inference_latency_ms',
    baselineValue: 2000,
    stdDeviation: 500,
    percentile95: 3000,
    percentile99: 5000,
    minValue: 800,
    maxValue: 10000,
    sampleCount: 25000,
  },

  DATABASE_QUERY_LATENCY: {
    id: SIGNAL_BASELINE_IDS.DATABASE_QUERY_LATENCY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    serviceName: 'case-management-service',
    signalType: 'db_query_latency_ms',
    baselineValue: 15,
    stdDeviation: 5,
    percentile95: 25,
    percentile99: 50,
    minValue: 1,
    maxValue: 100,
    sampleCount: 100000,
  },
};

// =============================================================================
// E2E SCENARIO DATA
// =============================================================================

export const E2E_SCENARIOS: Record<keyof typeof E2E_SCENARIO_IDS, E2EScenarioSeedData> = {
  FULL_LOAN_FLOW: {
    id: E2E_SCENARIO_IDS.FULL_LOAN_FLOW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    scenarioCode: 'LOAN_FULL_FLOW',
    name: 'Full Loan Application Flow',
    description: 'Complete loan application from consumer registration to funding',
    serviceFlow: ['2', '29', '30', '28', '26'], // Gateway -> CaseMgmt -> Entity -> Notification -> Billing
    signalThresholds: {
      execution_latency_ms: { max: 30000, p99: 25000 },
      db_query_latency_ms: { max: 100, p99: 50 },
      api_response_time_ms: { max: 5000, p99: 3000 },
    },
    maxDurationMs: 60000,
    assertions: [
      { type: 'status', path: 'loan.status', operator: 'eq', value: 'SUBMITTED' },
      { type: 'exists', path: 'loan.applicationId', operator: 'contains', value: 'LOAN-' },
      { type: 'count', path: 'documents', operator: 'gt', value: 0 },
    ],
    enabled: true,
    priority: 1,
    tags: ['loan', 'e2e', 'critical'],
  },

  DOCUMENT_PROCESSING: {
    id: E2E_SCENARIO_IDS.DOCUMENT_PROCESSING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    scenarioCode: 'DOC_PROCESS',
    name: 'Document Processing Pipeline',
    description: 'Document upload, OCR, extraction, and vector embedding',
    serviceFlow: ['2', '30', '5', '7'], // Gateway -> Entity -> Storage -> Retrieval
    signalThresholds: {
      processing_latency_ms: { max: 45000, p99: 40000 },
      embedding_latency_ms: { max: 5000, p99: 3000 },
    },
    maxDurationMs: 60000,
    assertions: [
      { type: 'status', path: 'document.status', operator: 'eq', value: 'PROCESSED' },
      { type: 'exists', path: 'document.embeddings', operator: 'gt', value: 0 },
    ],
    enabled: true,
    priority: 2,
    tags: ['document', 'ocr', 'rag'],
  },

  CONSUMER_ONBOARDING: {
    id: E2E_SCENARIO_IDS.CONSUMER_ONBOARDING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    scenarioCode: 'CONSUMER_ONBOARD',
    name: 'Consumer Onboarding Flow',
    description: 'New consumer registration, verification, and portal access',
    serviceFlow: ['2', '29', '24', '28'], // Gateway -> CaseMgmt -> Account -> Notification
    signalThresholds: {
      api_response_time_ms: { max: 3000, p99: 2000 },
      email_delivery_ms: { max: 10000 },
    },
    maxDurationMs: 30000,
    assertions: [
      { type: 'status', path: 'consumer.status', operator: 'eq', value: 'ACTIVE' },
      { type: 'exists', path: 'consumer.portalAccess', operator: 'eq', value: true },
    ],
    enabled: true,
    priority: 2,
    tags: ['consumer', 'onboarding', 'portal'],
  },

  AI_CHAT_SESSION: {
    id: E2E_SCENARIO_IDS.AI_CHAT_SESSION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    scenarioCode: 'AI_CHAT',
    name: 'AI Chat Session Flow',
    description: 'AI agent chat session with RAG-enhanced responses',
    serviceFlow: ['2', '8', '7', '6'], // Gateway -> Agent -> Retrieval -> ModelGateway
    signalThresholds: {
      agent_latency_ms: { max: 10000, p99: 8000 },
      retrieval_latency_ms: { max: 1000, p99: 500 },
      model_latency_ms: { max: 5000, p99: 4000 },
    },
    maxDurationMs: 20000,
    assertions: [
      { type: 'exists', path: 'response.message', operator: 'contains', value: '' },
      { type: 'quality', path: 'response.relevance', operator: 'gt', value: 0.7 },
    ],
    enabled: true,
    priority: 1,
    tags: ['ai', 'chat', 'rag', 'critical'],
  },

  VOICE_CAMPAIGN_FLOW: {
    id: E2E_SCENARIO_IDS.VOICE_CAMPAIGN_FLOW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    scenarioCode: 'VOICE_CAMPAIGN',
    name: 'Voice Campaign Execution',
    description: 'Outbound voice campaign with AI agent calls',
    serviceFlow: ['2', '34', '8', '6'], // Gateway -> CRM (voice) -> Agent -> ModelGateway
    signalThresholds: {
      call_setup_latency_ms: { max: 5000 },
      ai_response_latency_ms: { max: 2000, p99: 1500 },
    },
    maxDurationMs: 120000,
    assertions: [
      { type: 'status', path: 'call.status', operator: 'eq', value: 'COMPLETED' },
      { type: 'duration', path: 'call.duration', operator: 'gt', value: 10000 },
    ],
    enabled: true,
    priority: 3,
    tags: ['voice', 'campaign', 'retell'],
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllTestScenarios(workspace?: string): TestScenarioSeedData[] {
  const scenarios = Object.values(TEST_SCENARIOS);
  if (!workspace) return scenarios;
  return scenarios.map((s) => ({ ...s, name: `${s.name} [${workspace}]` }));
}

export function getAllIncidents(): IncidentSeedData[] {
  return Object.values(INCIDENTS);
}

export function getAllAlertConfigs(): AlertConfigSeedData[] {
  return Object.values(ALERT_CONFIGS);
}

export function getAllSignalBaselines(): SignalBaselineSeedData[] {
  return Object.values(SIGNAL_BASELINES);
}

export function getAllE2EScenarios(workspace?: string): E2EScenarioSeedData[] {
  const scenarios = Object.values(E2E_SCENARIOS);
  if (!workspace) return scenarios;
  return scenarios.map((s) => ({ ...s, name: `${s.name} [${workspace}]` }));
}

export function getTestScenariosByTag(tag: string): TestScenarioSeedData[] {
  return Object.values(TEST_SCENARIOS).filter((s) => s.tags.includes(tag));
}

export function getE2EScenariosByPriority(maxPriority: number): E2EScenarioSeedData[] {
  return Object.values(E2E_SCENARIOS).filter((s) => s.priority <= maxPriority);
}

export function getAlertConfigByBusiness(businessId: string): AlertConfigSeedData | undefined {
  return Object.values(ALERT_CONFIGS).find((c) => c.businessId === businessId);
}

export function getBaselinesByService(serviceName: string): SignalBaselineSeedData[] {
  return Object.values(SIGNAL_BASELINES).filter((b) => b.serviceName === serviceName);
}
